import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { TopographicPattern } from '../components/TopographicPattern';
export function WhereAIFits() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-763">
      {/* Hero */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-764">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-765">
          <BlueprintGrid data-id="element-766" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-767">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-768">
            Where AI Fits
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold mb-6 leading-tight" data-id="element-769">
            <span style={{
            color: '#5A6B7C'
          }} data-id="element-770">
              AI is the answer.
            </span>{' '}
            <span className="text-navy" data-id="element-771">
              But not to the question most people are asking.
            </span>
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-3xl mb-8 space-y-4" data-id="element-772">
            <p data-id="element-773">AI can absolutely create leverage.</p>
            <p data-id="element-774">
              It can improve speed, consistency, signal quality,
              troubleshooting, decision support, and bottom-line performance.
            </p>
            <p data-id="element-775">
              But it does not fix fuzzy rules, weak handoffs, scattered
              knowledge, or workflows nobody can define cleanly.
            </p>
            <p data-id="element-776">
              If the business logic is unclear, AI usually accelerates noise.
            </p>
            <p className="font-medium text-navy" data-id="element-777">
              If the workflow is sound, AI can become a real lever.
            </p>
          </motion.div>

          <motion.div variants={fadeIn} className="flex flex-col" data-id="element-778">
            <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 w-fit" data-id="element-779">
              <span data-id="element-780">Book an Honest Conversation</span>
              <ArrowRight className="w-5 h-5" data-id="element-781" />
            </Link>
            <p className="text-sm text-text-muted mt-2" data-id="element-782">
              45 minutes. No pitch. Just clarity.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* AI works best when attached to something real */}
      <section className="py-24 px-6 border-t hairline-top bg-paper-dark" data-id="element-783">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-784">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-785">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-786">
              AI works best when it is attached to something real
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-787">
            <p className="text-text" data-id="element-788">
              Most AI efforts do not stall because the model is weak.
            </p>
            <p className="text-text" data-id="element-789">
              They stall because the business is not clear enough yet.
            </p>
            <p className="text-text" data-id="element-790">
              The logic is still trapped in people. Definitions drift. Ownership
              is fuzzy. Exceptions are doing the real work. The team cannot
              explain what good looks like clearly enough for the system to
              support it.
            </p>
            <p className="text-text" data-id="element-791">
              That is why AI so often creates the appearance of progress without
              delivering operating leverage.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-792">
              Technology follows clarity. We do not automate confusion.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What AI-ready actually means */}
      <section id="ai-ready" className="relative py-24 px-6 bg-[#8BA89F26] grain border-y border-seaglass/20 overflow-hidden" data-id="element-793">
        <TopographicPattern color="sage" opacity={0.06} data-id="element-794" />

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-795">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-796">
            <p className="eyebrow mb-4 text-seaglass" data-id="element-797">AI-ready</p>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-798">
              What AI-ready actually means
            </h2>
            <div className="text-lg text-text space-y-4" data-id="element-799">
              <p data-id="element-800">
                AI-ready does not mean the company is modern, technical, or full
                of tools.
              </p>
              <p data-id="element-801">
                It means the workflow is clear enough for AI to support
                something real.
              </p>
              <p data-id="element-802">That usually requires:</p>
            </div>
          </motion.div>

          <motion.div variants={fadeIn} className="mb-10" data-id="element-803">
            <ul className="space-y-3 pl-1" data-id="element-804">
              {['clear definitions', 'clear decision rules', 'clear ownership', 'usable knowledge outside people\u2019s heads', 'clear guardrails around where AI helps and where humans still decide'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-805">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-806" />
                  <span className="text-body text-text" data-id="element-807">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div variants={fadeIn} data-id="element-808">
            <div className="border-l-2 border-seaglass pl-8 py-2 space-y-2" data-id="element-809">
              <p className="text-body text-text font-medium" data-id="element-810">
                If those are missing, the answer is usually not more AI.
              </p>
              <p className="text-body text-text font-medium" data-id="element-811">
                It is better structure first.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* How I use AI in this work */}
      <section className="py-24 px-6 bg-paper" data-id="element-812">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-813">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-814">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-815">
              How I use AI in this work
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-816">
            <p className="text-body text-text" data-id="element-817">
              I use AI to help find leverage and improve bottom-line
              performance.
            </p>

            <p className="text-body text-text" data-id="element-818">That can include:</p>

            <ul className="space-y-3" data-id="element-819">
              {['clarifying decision rules before automation', 'tightening handoffs so outputs do not die in the middle', 'cleaning up definitions so systems produce usable signals', 'redesigning exception paths that currently depend on judgment trapped in people', 'identifying where AI can support speed, consistency, routing, drafting, summarizing, troubleshooting, or signal detection without increasing confusion'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-820">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-821" />
                  <span className="text-body text-text" data-id="element-822">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-navy font-medium" data-id="element-823">
              The point is not to force AI into the business.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-824">
              The point is to make sure it is accelerating something real.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* A simple example */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-825">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-826">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-827">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-828">
              A simple example
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-829">
            <p className="text-body text-text" data-id="element-830">
              A founder had his team build a service ticket platform to improve
              the customer experience.
            </p>

            <p className="text-body text-text" data-id="element-831">
              The interface was fine. But the real leverage was not the UI.
            </p>

            <p className="text-body text-navy font-semibold" data-id="element-832">
              It was the clearer workflow truth underneath it.
            </p>

            <p className="text-body text-text" data-id="element-833">
              Once service data and account data were lined up properly, the
              business could finally see which requests came back inside 30 days
              of onboarding, where installation quality was breaking down
              upstream, and which industries were consuming the most service
              time relative to the revenue they produced.
            </p>

            <p className="text-body text-text" data-id="element-834">
              That created signals the business could actually trust.
            </p>

            <div className="py-8 my-4 border-y hairline-top hairline-bottom space-y-4" data-id="element-835">
              <p className="text-body text-text" data-id="element-836">
                Only after that did AI become useful in the next layer.
              </p>
              <p className="text-body text-text" data-id="element-837">
                With cleaner workflow truth in place, we built a custom GPT for
                field service agents using equipment manuals as reference
                material so they could troubleshoot faster from their phones.
              </p>
            </div>

            <p className="text-body text-navy font-semibold" data-id="element-838">
              That is where AI actually earns its role.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* What not to do */}
      <section className="py-24 px-6 bg-paper" data-id="element-839">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-840">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-841">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-842">
              What not to do
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-843">
            <ul className="space-y-3" data-id="element-844">
              {['Do not ask AI to fix a business that still runs on unclear rules.', 'Do not automate a workflow nobody can define cleanly.', 'Do not mistake tool activity for operating leverage.', 'Do not hand a model the job of interpreting what the business itself has not clarified yet.'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-845">
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-846" />
                  <span className="text-text" data-id="element-847">{item}</span>
                </li>)}
            </ul>

            <p className="text-text font-medium text-navy" data-id="element-848">
              Fix the recurring failure first.
            </p>
            <p className="text-text font-medium text-navy" data-id="element-849">
              Then decide what to automate.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* The point */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-850">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-851">
          <motion.div variants={fadeIn} className="border-l-2 border-seaglass pl-8 py-2" data-id="element-852">
            <p className="eyebrow mb-3 text-seaglass" data-id="element-853">The point</p>
            <p className="text-body text-text mb-3" data-id="element-854">
              If AI is not helping yet, the problem may not be AI.
            </p>
            <p className="text-body text-text mb-3" data-id="element-855">
              It may be that the workflow, ownership, definitions, or business
              logic are still too unclear to support it.
            </p>
            <p className="text-body text-navy font-medium mb-3" data-id="element-856">
              AI can be a real lever.
            </p>
            <p className="text-body text-navy font-medium" data-id="element-857">
              But only when it is attached to something the business can already
              trust.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-6 bg-navy grain overflow-hidden" data-id="element-858">
        <div className="absolute inset-0 text-white/[0.05]" data-id="element-859">
          <BlueprintGrid data-id="element-860" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeIn} className="relative max-w-3xl mx-auto text-center z-10" data-id="element-861">
          <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-862">
            <span style={{
            color: '#BBBFC4'
          }} data-id="element-863">
              Fix the recurring failure first.
            </span>{' '}
            <span className="text-white" data-id="element-864">Then decide what to automate.</span>
          </h2>
          <p className="text-lg text-white/80 mb-10" data-id="element-865">
            If AI is not helping yet, the problem may not be AI.
          </p>
          <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300 group" data-id="element-866">
            <span data-id="element-867">Book an Honest Conversation</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" data-id="element-868" />
          </Link>
          <p className="text-sm text-white/55 mt-4" data-id="element-869">
            45 minutes. No pitch. Just clarity.
          </p>
        </motion.div>
      </section>
    </main>;
}